// Раскладка списка норм по группам ответа.
//
// Группу назначает сервер (`group` в `/cabinet/rules`); здесь — только порядок.
// Нормы спорной пары стоят рядом: противоречие читается парой, и разнесённые
// по списку половины никто не сведёт глазами. Общего итога «можно / нельзя»
// нет: запрет и разрешение стоят каждый в своей группе.

import type { RuleNorm } from '../api'

export type RuleGroup = 'applies' | 'depends' | 'conflict' | 'excepted' | 'idle'

/** Порядок групп: что действует — первым, что не касается — последним. */
export const GROUP_ORDER: RuleGroup[] = ['applies', 'depends', 'conflict', 'excepted', 'idle']

/** Группы, свёрнутые по умолчанию: «не касается» нужна, чтобы ничего не
 *  исчезало молча, но читать её первой незачем. */
export const COLLAPSED_BY_DEFAULT: ReadonlySet<RuleGroup> = new Set(['idle'])

export interface GroupView {
  key: RuleGroup
  norms: RuleNorm[]
}

/** Порядок внутри группы: места под точкой (клик был про них), задетые краем,
 *  нормы без места, и последними места без координат — реки и заливы района,
 *  про которые известно только «может быть здесь». */
const PLACE_RANK: Record<string, number> = { here: 0, edge: 1, everywhere: 2, unknown: 3 }

function hereFirst(a: RuleNorm, b: RuleNorm): number {
  const rank = (n: RuleNorm) => PLACE_RANK[n.place ?? ''] ?? 2
  return rank(a) - rank(b)
}

function pairAdjacent(norms: RuleNorm[]): RuleNorm[] {
  const byCode = new Map(norms.map(n => [n.code, n]))
  const placed = new Set<string>()
  const out: RuleNorm[] = []
  for (const n of norms) {
    if (placed.has(n.code)) continue
    out.push(n)
    placed.add(n.code)
    for (const c of n.conflicts) {
      const other = c.with ? byCode.get(c.with) : undefined
      if (other && !placed.has(other.code)) {
        out.push(other)
        placed.add(other.code)
      }
    }
  }
  return out
}

export function groupNorms(norms: RuleNorm[]): GroupView[] {
  const by = new Map<RuleGroup, RuleNorm[]>()
  for (const n of norms) {
    const g = (GROUP_ORDER as string[]).includes(n.group) ? n.group as RuleGroup : 'idle'
    by.set(g, [...(by.get(g) ?? []), n])
  }
  return GROUP_ORDER
    .filter(g => by.has(g))
    .map(g => {
      const sorted = [...by.get(g)!].sort(hereFirst)
      return { key: g, norms: g === 'conflict' ? pairAdjacent(sorted) : sorted }
    })
}

/** Скобки группы «противоречие»: соседние нормы, связанные парой, — одна
 *  скобка; одиночная находка — скобка из одной нормы. */
export function pairChunks(norms: RuleNorm[]): RuleNorm[][] {
  const out: RuleNorm[][] = []
  for (const n of norms) {
    const last = out[out.length - 1]
    const linked = last?.some(m => m.conflicts.some(c => c.with === n.code)
      || n.conflicts.some(c => c.with === m.code))
    if (linked) last.push(n)
    else out.push([n])
  }
  return out
}

export interface LedgerSegment {
  key: RuleGroup
  count: number
  /** Доля среди касающихся норм, %. */
  pct: number
}

/** Сводка вердиктов для полосы над списком: сколько норм в каждой группе и
 *  какая это доля. «Не касается» в доли не входит — иначе в широком кадре без
 *  точки она съела бы полосу целиком, а читают полосу ради касающихся. */
export function verdictLedger(norms: RuleNorm[]): LedgerSegment[] {
  const groups = groupNorms(norms).filter(g => g.key !== 'idle')
  const total = groups.reduce((s, g) => s + g.norms.length, 0)
  if (!total) return []
  return groups.map(g => ({ key: g.key, count: g.norms.length, pct: (g.norms.length / total) * 100 }))
}
